import { BrandLogo, Spinner } from '@/shared/ui';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAppConfig } from '../../config/appConfigRef';
import { cleanHandoffParamsFromUrl, extractHandoffParams } from '../handoffUrl';
import { clearPkceSession, verifyOAuthState } from '../pkce';
import { useAuthStore } from '../authStore';

export interface LoginSuccessPageProps {
  logoSrc?: string;
  redirectTo?: string;
}

export function LoginSuccessPage({ logoSrc, redirectTo = '/' }: LoginSuccessPageProps) {
  const navigate = useNavigate();
  const redeemCallback = useAuthStore((s) => s.redeemCallback);
  const startLoginRedirect = useAuthStore((s) => s.startLoginRedirect);
  const appName = getAppConfig().appName;

  useEffect(() => {
    const handleCallback = async () => {
      const { code, state } = extractHandoffParams();

      // Check state before anything else, and clear both stored values whatever happens.
      if (!code || !verifyOAuthState(state)) {
        clearPkceSession();
        cleanHandoffParamsFromUrl();
        void startLoginRedirect();
        return;
      }

      try {
        await redeemCallback(code);
        useAuthStore.getState().initialize();
        cleanHandoffParamsFromUrl();
      } catch (error) {
        // The code is burned before any check runs, so retrying never helps — restart.
        console.error('Hand-off redemption failed:', error);
        cleanHandoffParamsFromUrl();
        void startLoginRedirect();
        return;
      }

      const { status } = useAuthStore.getState();
      if (status === 'authenticated' || status === 'forbidden') {
        void navigate(redirectTo, { replace: true });
        return;
      }

      void startLoginRedirect();
    };

    void handleCallback();
  }, [navigate, redirectTo, redeemCallback, startLoginRedirect]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--cream-0)]">
      <div className="flex flex-col items-center gap-4">
        <div className="flex items-center gap-2">
          {logoSrc ? <BrandLogo src={logoSrc} alt={appName} className="h-8 w-8" /> : null}
          <span className="font-[var(--font-display)] text-lg font-semibold text-[var(--ink-0)]">
            {appName}
          </span>
        </div>
        <div className="space-y-1 text-center">
          <h1 className="text-xl font-semibold text-[var(--ink-0)]">Signing you in</h1>
          <p className="text-sm text-[var(--ink-2)]">Completing authentication…</p>
        </div>
        <Spinner size={24} />
      </div>
    </div>
  );
}
